import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Col, Input, Row, Select } from 'antd';

import timeZones from 'v3/utils/timeZones.json';
import { Button } from 'v3/components/pitstop';
import { PitstopModal } from 'shared';
import { AppStore, CarStore, CurrentUserStore } from 'stores';

const Container = styled.div`
  text-align: center;

  h3 {
    font-weight: 600;
    font-size: 1.6rem;
  }

  label {
    display: block;
    text-align: left;
    width: 100%;
    font-weight: 600;
    margin: 10px 0px;
  }

  .ant-select {
    width: 100%;
  }

  input,
  .ant-select,
  .ant-select-selection--single {
    height: 48px;
  }

  .ant-select-selection__rendered {
    margin-top: 10px;
  }
`;

const BoxSelection = styled.div`
  border-radius: 5px;
  border: 1px solid #000000;
  height: 48px;
  cursor: pointer;

  display: flex;
  justify-content: center;
  align-items: center;

  background-color: ${(props) => props.selected && '#02103d'};
  color: ${(props) => props.selected && '#ffffff'};
`;

const ProfileForm = ({ id }) => {
  const modalId = id;
  const [formData, setFormData] = useState({
    companyName: null,
    companyPhone: null,
    timezone: null,
    fleetSize: '50 < 100',
    odometer: 'km',
    fuel: 'Litre',
  });

  const { companyName, companyPhone, timezone, fleetSize, odometer, fuel } =
    formData;

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProfileUpdate = async () => {
    try {
      AppStore.addLoading('Updating your profile...');

      await CurrentUserStore.updateHubspotContact({
        company: companyName,
        phone: companyPhone,
      });

      await CurrentUserStore.user.updateSettings({
        timezone,
        odometer,
        fuelUnit: fuel,
      });

      AppStore.openModals.set(modalId, false);
      CarStore.isReloaded = true;
    } catch {
      AppStore.addError('Error in updating your profile!');
    } finally {
      AppStore.removeLoading('Updating your profile...');
    }
  };

  return (
    <PitstopModal id={modalId} width={1030} footer={null}>
      <Container>
        <Row type="flex" justify="center" style={{ padding: '50px 0px' }}>
          <Col span={16}>
            <Row style={{ margin: '15px 0px' }}>
              <h3>Complete Your Profile</h3>
            </Row>

            <Row gutter={[16, 48]}>
              <Col span={12}>
                <label>Company Name</label>
                <Input
                  name="companyName"
                  placeholder="ABC Fleet"
                  onChange={(e) =>
                    handleInputChange('companyName', e.target.value)
                  }
                />
              </Col>

              <Col span={12}>
                <label>Timezone</label>
                <Select
                  showSearch
                  placeholder="Timezone"
                  defaultValue="America/Toronto"
                  onChange={(val) => handleInputChange('timezone', val)}
                >
                  {Object.keys(timeZones).map((key) => (
                    <Select.Option key={key} value={key}>
                      {timeZones[key]}
                    </Select.Option>
                  ))}
                </Select>
              </Col>

              <Col span={12}>
                <label>Company Phone Number</label>
                <Input
                  name="companyPhone"
                  placeholder="+000-000-0000"
                  onChange={(e) =>
                    handleInputChange('companyPhone', e.target.value)
                  }
                />
              </Col>

              <Col span={12}>
                <label>Fleet Size</label>
                <Row gutter={[16, 16]}>
                  {['0 < 20', '20 < 50', '50 < 100', '> 100'].map((size) => (
                    <Col span={6} key={size}>
                      <BoxSelection
                        selected={fleetSize === size}
                        onClick={() => handleInputChange('fleetSize', size)}
                      >
                        {size}
                      </BoxSelection>
                    </Col>
                  ))}
                </Row>
              </Col>

              <Col span={12}>
                <label>Odometer Unit</label>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <BoxSelection
                      selected={odometer === 'km'}
                      onClick={() => handleInputChange('odometer', 'km')}
                    >
                      Kilometer (km)
                    </BoxSelection>
                  </Col>
                  <Col span={12}>
                    <BoxSelection
                      selected={odometer === 'mi'}
                      onClick={() => handleInputChange('odometer', 'mi')}
                    >
                      Miles (mi)
                    </BoxSelection>
                  </Col>
                </Row>
              </Col>

              <Col span={12}>
                <label>Fuel Unit</label>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <BoxSelection
                      selected={fuel === 'Litre'}
                      onClick={() => handleInputChange('fuel', 'Litre')}
                    >
                      Litre
                    </BoxSelection>
                  </Col>
                  <Col span={12}>
                    <BoxSelection
                      selected={fuel === 'Gallon'}
                      onClick={() => handleInputChange('fuel', 'Gallon')}
                    >
                      Gallon
                    </BoxSelection>
                  </Col>
                </Row>
              </Col>
            </Row>

            <Row style={{ marginBottom: 20 }}>
              <Button onClick={handleProfileUpdate}>
                <Link to="/report">Connect to Dashboard</Link>
              </Button>
            </Row>
          </Col>
        </Row>
      </Container>
    </PitstopModal>
  );
};

ProfileForm.propTypes = {
  id: PropTypes.string.isRequired,
};

export default ProfileForm;
